import React, { useEffect, useMemo, useState } from 'react'
import { IconButton, Typography } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { Container } from '../../../../../components'
import {
    ezScheduleDetails,
    TemplateService,
} from '../../../../../models/WorkOrder/types'
import { useScheduleReviewStyles } from '../styles'

interface Props {
    templateServices: TemplateService[]
    ezSchedule: ezScheduleDetails
    selectedPhaseId?: number
}

interface VisibleVendor {
    id: number
    name: string
    startDate: string | null
    endDate: string | null
    startDay: number | null
    endDay: number | null
}

interface VisibleService {
    id: number
    name: string
    position: number
    vendors: VisibleVendor[]
}

type ValidationIssueCode =
    | 'start-before-ready'
    | 'insufficient-gap-after-prior-service'

interface ValidationIssue {
    code: ValidationIssueCode
    message: string
}

/**
 * Parse a date string to a numeric day key (UTC calendar day) for comparison.
 * Uses only the date part (first 10 chars, YYYY-MM-DD); time, milliseconds,
 * and timezone in the string are ignored. So e.g. "2026-04-08T10:00:00.999-10:00"
 * and "2026-04-08" produce the same day key.
 */
const parseDateToDayKey = (value: string | null | undefined): number | null => {
    if (!value) {
        return null
    }

    const normalized = value.slice(0, 10)
    const [yearStr, monthStr, dayStr] = normalized.split('-')
    const year = Number(yearStr)
    const month = Number(monthStr)
    const day = Number(dayStr)

    if (
        !Number.isInteger(year) ||
        !Number.isInteger(month) ||
        !Number.isInteger(day)
    ) {
        return null
    }

    const utcMs = Date.UTC(year, month - 1, day)
    const parsed = new Date(utcMs)

    if (
        parsed.getUTCFullYear() !== year ||
        parsed.getUTCMonth() !== month - 1 ||
        parsed.getUTCDate() !== day
    ) {
        return null
    }

    return Math.floor(utcMs / (24 * 60 * 60 * 1000))
}

const formatDayKey = (dayKey: number): string => {
    const utcMs = dayKey * 24 * 60 * 60 * 1000
    return new Date(utcMs).toISOString().slice(0, 10)
}

const buildVisibleSchedule = (
    templateServices: TemplateService[],
): VisibleService[] => {
    const orderedServices = [...templateServices]
        .filter((service) => !service.stand_alone)
        .sort((left, right) => left.position - right.position)

    return orderedServices.map((service) => ({
        id: service.id,
        name: service.service?.name || `Service ${service.id}`,
        position: service.position,
        vendors: service.template_service_vendors.map((vendor) => ({
            id: vendor.id,
            name: vendor.vendor?.name || 'Unknown Vendor',
            startDate: vendor.start_date ?? null,
            endDate: vendor.end_date ?? null,
            startDay: parseDateToDayKey(vendor.start_date),
            endDay: parseDateToDayKey(vendor.end_date),
        })),
    }))
}

const getLatestVendorEndDay = (service: VisibleService): number | null => {
    return service.vendors.reduce<number | null>((latestEndDay, vendor) => {
        if (vendor.endDay === null) {
            return latestEndDay
        }

        if (latestEndDay === null || vendor.endDay > latestEndDay) {
            return vendor.endDay
        }

        return latestEndDay
    }, null)
}

/** Latest end timestamp (ms) among all vendors; used for "ends after" comparison. */
const getLatestVendorEndTimestamp = (
    service: VisibleService,
): number | null => {
    return service.vendors.reduce<number | null>((latestMs, vendor) => {
        if (!vendor.endDate) return latestMs
        const ms = Date.parse(vendor.endDate)
        if (Number.isNaN(ms)) return latestMs
        if (latestMs === null || ms > latestMs) return ms
        return latestMs
    }, null)
}

const getEarliestVendorStartDay = (service: VisibleService): number | null => {
    return service.vendors.reduce<number | null>((earliestStartDay, vendor) => {
        if (vendor.startDay === null) {
            return earliestStartDay
        }

        if (earliestStartDay === null || vendor.startDay < earliestStartDay) {
            return vendor.startDay
        }

        return earliestStartDay
    }, null)
}

const validateStartBeforeReady = (
    services: VisibleService[],
): ValidationIssue[] => {
    const issues: ValidationIssue[] = []

    for (let index = 1; index < services.length; index += 1) {
        const previousService = services[index - 1]
        const currentService = services[index]
        const previousEarliestStartDay = getEarliestVendorStartDay(
            previousService,
        )

        if (previousEarliestStartDay === null) {
            continue
        }

        const minAllowedStartDay = previousEarliestStartDay + 1
        const minAllowedStartDate = formatDayKey(minAllowedStartDay)

        currentService.vendors.forEach((vendor) => {
            if (
                vendor.startDay === null ||
                vendor.startDay >= minAllowedStartDay
            ) {
                return
            }

            issues.push({
                code: 'start-before-ready',
                message: `Warning: ${vendor.name} for ${currentService.name} starts before units are ready. Earliest valid start is ${minAllowedStartDate}, one day after ${previousService.name} begins.`,
            })
        })
    }

    return issues
}

const validateInsufficientGapAfterPriorService = (
    services: VisibleService[],
): ValidationIssue[] => {
    const issues: ValidationIssue[] = []

    for (let index = 1; index < services.length; index += 1) {
        const previousService = services[index - 1]
        const currentService = services[index]
        const previousLatestEndMs = getLatestVendorEndTimestamp(previousService)

        if (previousLatestEndMs === null) {
            continue
        }

        const previousLatestEndDate = new Date(previousLatestEndMs)
            .toISOString()
            .slice(0, 10)

        currentService.vendors.forEach((vendor) => {
            const vendorEndMs = vendor.endDate
                ? Date.parse(vendor.endDate)
                : NaN
            if (
                Number.isNaN(vendorEndMs) ||
                vendorEndMs > previousLatestEndMs
            ) {
                return
            }

            issues.push({
                code: 'insufficient-gap-after-prior-service',
                message: `Warning: ${vendor.name} for ${currentService.name} ends too early. End date must be at least one day after ${previousService.name} latest end (${previousLatestEndDate}).`,
            })
        })
    }

    return issues
}

/**
 * Serialize vendor dates so validation re-runs when any date changes.
 * Computed during render (not memoized) so we pick up in-place updates or
 * cache updates where templateServices reference might not change.
 */
const getTemplateServicesDateSignature = (
    templateServices: TemplateService[],
): string =>
    JSON.stringify(
        templateServices
            .filter((s) => !s.stand_alone)
            .flatMap((s) =>
                (s.template_service_vendors ?? []).map((v) => [
                    v.start_date,
                    v.end_date,
                ]),
            ),
    )

export const ErrorPanel = ({
    templateServices,
    ezSchedule,
    selectedPhaseId,
}: Props) => {
    const classes = useScheduleReviewStyles()
    const [activeIssueIndex, setActiveIssueIndex] = useState(0)
    const templateServicesDateSignature = getTemplateServicesDateSignature(
        templateServices,
    )

    const totalServiceAreas = useMemo(() => {
        const phase =
            selectedPhaseId !== undefined
                ? ezSchedule.phases.find((p) => p.phase?.id === selectedPhaseId)
                : ezSchedule.phases[0]
        return phase?.total_service_areas ?? 0
    }, [ezSchedule.phases, selectedPhaseId])

    const validationIssues = useMemo(() => {
        const visibleServices = buildVisibleSchedule(templateServices)
        const issues: ValidationIssue[] = []

        const workStartDate = ezSchedule.work_start_date
        const workEndDate = ezSchedule.work_end_date
        const workStartDay = parseDateToDayKey(workStartDate)
        const workEndDay = parseDateToDayKey(workEndDate)

        if (!workStartDate) {
            return issues
        }

        if (
            !workStartDate ||
            !workEndDate ||
            workStartDay === null ||
            workEndDay === null
        ) {
            return issues
        }

        if (visibleServices.length === 0) {
            return issues
        }

        issues.push(...validateStartBeforeReady(visibleServices))
        issues.push(
            ...validateInsufficientGapAfterPriorService(visibleServices),
        )

        return issues
    }, [
        ezSchedule.work_end_date,
        ezSchedule.work_start_date,
        templateServicesDateSignature,
    ])

    useEffect(() => {
        setActiveIssueIndex(0)
    }, [validationIssues])

    const hasIssues = validationIssues.length > 0
    const currentIssue = hasIssues ? validationIssues[activeIssueIndex] : null
    const isOnFirstIssue = activeIssueIndex <= 0
    const isOnLastIssue = activeIssueIndex >= validationIssues.length - 1
    const hasNoServiceAreas = totalServiceAreas === 0

    return (
        <Container className={classes.errorPanelRoot}>
            {hasNoServiceAreas ? (
                <Typography
                    variant="body2"
                    className={classes.errorPanelWarningText}
                >
                    No service areas to schedule. Sync bed/area statuses first.
                </Typography>
            ) : !hasIssues ? (
                <Typography
                    variant="body2"
                    className={classes.errorPanelSuccessText}
                >
                    No scheduling date conflicts detected.
                </Typography>
            ) : (
                <Container className={classes.errorPanelContent}>
                    <Container className={classes.errorPanelLeftRail}>
                        <Typography
                            variant="subtitle2"
                            color="error"
                            className={classes.errorPanelTitle}
                        >
                            {validationIssues.length} issue(s)
                        </Typography>
                        <Container className={classes.errorPanelPagerRow}>
                            <IconButton
                                size="small"
                                className={classes.errorPanelPagerArrow}
                                disabled={isOnFirstIssue}
                                onClick={() =>
                                    setActiveIssueIndex((current) =>
                                        Math.max(0, current - 1),
                                    )
                                }
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <Typography
                                variant="caption"
                                className={classes.errorPanelPagerIndex}
                            >
                                {activeIssueIndex + 1} /{' '}
                                {validationIssues.length}
                            </Typography>
                            <IconButton
                                size="small"
                                className={classes.errorPanelPagerArrow}
                                disabled={isOnLastIssue}
                                onClick={() =>
                                    setActiveIssueIndex((current) =>
                                        Math.min(
                                            validationIssues.length - 1,
                                            current + 1,
                                        ),
                                    )
                                }
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Container>
                    </Container>
                    <Container className={classes.errorPanelMessagePane}>
                        <Typography
                            variant="body2"
                            className={classes.errorPanelMessage}
                        >
                            {currentIssue?.message}
                        </Typography>
                    </Container>
                </Container>
            )}
        </Container>
    )
}
